// Cards (aba da Cyclopedia): cartas colecionáveis das criaturas. A carta sai na etapa 2 do bestiário (2.500 abates);
// o jogador monta um deck com os espaços liberados pelo level. Raridade pela dificuldade, bônus pela classe.
import { useMemo, useState } from 'preact/hooks';
import { BESTIARY_KILLS, bestiaryStage, type CharmState } from '../../../../shared/src/game/charms';
import { CARD_RARITY_NAMES, CARD_VALUE, cardRarity, cardSlots, cardStat, type CardRarity } from '../../../../shared/src/game/cards';
import { AFFIX_NAMES, type AffixKey } from '../../../../shared/src/game/affixes';
import { Portrait } from './BattleList';
import type { BestiaryEntry } from './Cyclopedia';

const n = (v: number) => v.toLocaleString('pt-BR');
const pct = (v: number) => String(Math.round(v * 100) / 100).replace('.', ',');
const NEED = BESTIARY_KILLS[1];
/** levels que liberam os espaços extras (igual a cardSlots) */
const SLOT_LEVELS = [0, 0, 0, 200, 500];
const RARITY_ICON = ['◆', '◆◆', '◆◆◆', '♛'];

function Card(props: { d: BestiaryEntry; on?: boolean; onClick?: () => void; label?: string; tag?: string; big?: boolean }) {
  const r = cardRarity(props.d), st = cardStat(props.d);
  const stars = Math.max(1, Math.min(5, props.d.stars ?? 1));
  return (
    <button class={'cd-card cd-r' + r + (props.on ? ' cd-on' : '') + (props.big ? ' cd-big' : '') + (props.onClick ? '' : ' cd-static')}
      onClick={props.onClick} title={props.label}>
      <span class="cd-frame">
        <span class="cd-top">
          <b class="cd-name">{props.d.name}</b>
          <span class="cd-gem" title={CARD_RARITY_NAMES[r]} />
        </span>
        <span class="cd-art">
          <span class="cd-art-glow" />
          <Portrait outfit={props.d.outfit} size={64} />
        </span>
        <span class="cd-band">{CARD_RARITY_NAMES[r]} <i>·</i> {props.d.class}</span>
        <span class="cd-stars" title={`Dificuldade ${stars} de 5`}>{'★'.repeat(stars)}<em>{'★'.repeat(5 - stars)}</em></span>
        <span class="cd-bonus"><b>+{pct(CARD_VALUE[r])}%</b> {AFFIX_NAMES[st]}</span>
      </span>
      <span class="cd-foil" />
      {props.tag && <span class="cd-tag">{props.tag}</span>}
    </button>
  );
}

export function Cards(props: { data: BestiaryEntry[]; kills: Record<string, number>; charms: CharmState; level: number; send: (op: 'card' | 'uncard', id: string, monster?: string) => void }) {
  const [help, setHelp] = useState(false);
  const [filter, setFilter] = useState<CardRarity | -1>(-1);
  const eq = props.charms.cards ?? [];
  const slots = cardSlots(props.level);
  const maxSlots = SLOT_LEVELS.length;

  const owned = useMemo(() => props.data.filter((d) => bestiaryStage(props.kills[d.name] ?? 0, d) >= 2)
    .sort((a, b) => cardRarity(b) - cardRarity(a) || a.name.localeCompare(b.name)), [props.data, props.kills]);
  const byRarity = useMemo(() => {
    const c = [0, 0, 0, 0], t = [0, 0, 0, 0];
    for (const d of props.data) t[cardRarity(d)]++;
    for (const d of owned) c[cardRarity(d)]++;
    return { c, t };
  }, [props.data, owned]);
  const soon = useMemo(() => props.data
    .map((d) => ({ d, k: props.kills[d.name] ?? 0 }))
    .filter((x) => x.k > 0 && x.k < NEED)
    .sort((a, b) => b.k - a.k || cardRarity(b.d) - cardRarity(a.d))
    .slice(0, 6), [props.data, props.kills]);

  const equipped = Array.from({ length: slots }, (_, i) => props.data.find((x) => x.name === eq[i]));
  const totals = useMemo(() => {
    const m = new Map<AffixKey, number>();
    for (const d of equipped) if (d) { const k = cardStat(d); m.set(k, (m.get(k) ?? 0) + CARD_VALUE[cardRarity(d)]); }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [equipped.map((d) => d?.name ?? '').join('|')]);
  const used = equipped.filter(Boolean).length;
  const full = used >= slots;
  const shown = filter === -1 ? owned : owned.filter((d) => cardRarity(d) === filter);
  const nextLock = SLOT_LEVELS.find((l) => l > props.level);

  return (
    <div class="cd-root">
      {/* cabeçalho: o álbum */}
      <div class="cd-head">
        <div class="cd-fan" aria-hidden="true"><i /><i /><i /></div>
        <div class="cd-head-txt">
          <div class="cd-title">Cartas de Criaturas <button class="cd-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="cd-sub">Cada criatura dominada vira uma carta. Monte seu deck e leve o poder delas para a batalha.</div>
          <div class="cd-meter" title={`${owned.length} de ${props.data.length} cartas liberadas`}>
            <i style={{ width: (props.data.length ? (owned.length / props.data.length) * 100 : 0) + '%' }} />
            <span>{n(owned.length)} de {n(props.data.length)} liberadas</span>
          </div>
        </div>
        <div class="cd-head-stat" title="Espaços do deck em uso">
          <small>Deck</small><b>{used}<em>/{slots}</em></b>
          <span>{nextLock ? `+1 no level ${nextLock}` : 'todos liberados'}</span>
        </div>
      </div>

      {(help || !owned.length) && (
        <div class="cd-steps">
          <div><i>⚔️</i><b>1. Cace</b><small>Cada abate conta no bestiário da criatura. Veja o progresso em "Quase lá".</small></div>
          <div><i>🃏</i><b>2. Libere</b><small>Com {n(NEED)} abates (etapa 2 do bestiário) a carta dela entra na sua coleção.</small></div>
          <div><i>💎</i><b>3. Raridade</b><small>Quanto mais difícil a criatura, mais rara a carta: Comum +0,5%, Rara +1%, Épica +1,5%, Lendária +2%.</small></div>
          <div><i>🛡️</i><b>4. Equipe</b><small>Clique numa carta para pôr no deck ({slots} espaços; mais 1 no level 200 e no 500). Clique no deck para tirar.</small></div>
        </div>
      )}

      {/* o deck */}
      <div class="cd-sec">Seu deck <small>· clique numa carta do deck para tirar</small></div>
      <div class="cd-deck">
        <div class="cd-slots">
          {Array.from({ length: maxSlots }, (_, i) => {
            if (i >= slots) return <div key={i} class="cd-slot cd-locked" title={`Libera no level ${SLOT_LEVELS[i]}`}><span>🔒</span><b>Level {SLOT_LEVELS[i]}</b><small>libera este espaço</small></div>;
            const d = equipped[i];
            return d ? <Card key={i} d={d} on big label="Clique para tirar do deck" onClick={() => props.send('uncard', '', d.name)} />
              : <div key={i} class="cd-slot cd-free" title="Espaço livre: clique numa carta da coleção"><span>+</span><b>Espaço livre</b><small>escolha na coleção</small></div>;
          })}
        </div>
        <div class="cd-total">
          <b>Bônus do deck</b>
          {totals.length ? totals.map(([k, v]) => <span key={k}><em>+{pct(v)}%</em> {AFFIX_NAMES[k]}</span>)
            : <small>Nenhuma carta equipada.</small>}
          <small class="cd-note">Soma com o equipamento, respeitando o teto de cada atributo.</small>
        </div>
      </div>

      {/* coleção */}
      <div class="cd-sec">Coleção <small>· {n(owned.length)} de {n(props.data.length)} liberadas{full && owned.length > used ? ' · deck cheio: tire uma carta para trocar' : ''}</small></div>
      <div class="cd-filters">
        <button class={'tb-button' + (filter === -1 ? ' green' : '')} onClick={() => setFilter(-1)}>Todas ({owned.length})</button>
        {([0, 1, 2, 3] as CardRarity[]).map((r) => (
          <button key={r} class={'tb-button cd-fr' + r + (filter === r ? ' green' : '')} onClick={() => setFilter(r)}
            title={`${byRarity.c[r]} de ${byRarity.t[r]} cartas ${CARD_RARITY_NAMES[r]}s liberadas`}>
            <i>{RARITY_ICON[r]}</i> {CARD_RARITY_NAMES[r]} <small>{byRarity.c[r]}/{byRarity.t[r]}</small>
          </button>
        ))}
      </div>
      <div class="cd-grid">
        {shown.map((d) => {
          const on = eq.includes(d.name);
          return <Card key={d.name} d={d} on={on} tag={on ? 'No deck' : undefined}
            label={on ? 'Já está no deck' : full ? 'Deck cheio: tire uma carta antes' : 'Clique para equipar'}
            onClick={on ? undefined : () => props.send('card', '', d.name)} />;
        })}
        {!owned.length && <p class="cd-empty">Nenhuma carta ainda: mate {n(NEED)} de uma criatura para liberar a carta dela.</p>}
        {!!owned.length && !shown.length && <p class="cd-empty">Nenhuma carta {CARD_RARITY_NAMES[filter as CardRarity]} liberada ainda.</p>}
      </div>

      {/* próximas cartas */}
      <div class="cd-sec">Quase lá <small>· as criaturas mais perto de virar carta</small></div>
      <div class="cd-soon">
        {soon.map(({ d, k }) => {
          const r = cardRarity(d), p = Math.min(100, (k / NEED) * 100);
          return (
            <div key={d.name} class={'cd-next cd-r' + r} title={`${d.name}: ${n(k)} de ${n(NEED)} abates`}>
              <span class="cd-next-art"><Portrait outfit={d.outfit} size={48} /></span>
              <div class="cd-next-txt">
                <b>{d.name}</b>
                <small><span class="cd-rname">{CARD_RARITY_NAMES[r]}</span> · +{pct(CARD_VALUE[r])}% {AFFIX_NAMES[cardStat(d)]}</small>
                <div class="cd-bar"><i style={{ width: p + '%' }} /><span>{n(k)} / {n(NEED)}</span></div>
                <small class="cd-left">faltam {n(NEED - k)} abates</small>
              </div>
            </div>
          );
        })}
        {!soon.length && <p class="cd-empty">Comece a caçar: as criaturas com abates aparecem aqui com o progresso até a carta.</p>}
      </div>
    </div>
  );
}
